/**
 * ClienteContext — cliente ATIVO no Portal (B1.6 multi-cliente).
 *
 * Distinto do ClienteAuthContext (que cuida da sessão = quem é o user +
 * quais clientes ele tem). Esse cuida do "qual cliente ele tá navegando
 * agora" — usado pra setar header X-Client-Context automático em todas
 * chamadas à API que precisam de contexto.
 *
 * Lógica:
 *   - Lê `clientes` do ClienteAuthContext
 *   - Resolve cliente ativo:
 *     1. Tenta `cliente_ativo_id` no localStorage (preferência UI — ÚNICA
 *        exceção permitida pela Regra inviolável ZERO localStorage)
 *     2. Se inválido/ausente, usa primeiro cliente da lista
 *     3. Se 0 clientes, retorna null
 *   - Sincroniza com cliente-api.ts via setActiveClientContext
 *   - Persiste em localStorage quando trocar (preferência UI)
 *
 * Trocar de cliente:
 *   - Atualiza state
 *   - Atualiza localStorage
 *   - Atualiza setActiveClientContext
 *   - Invalida React Query (recarrega dashboard/chamados/etc com novo contexto)
 *
 * NÃO REVERTER:
 *   - localStorage SÓ pra `cliente_ativo_id` (preferência UI única exceção)
 *   - Sincroniza com setActiveClientContext SEMPRE (sem isso, chamadas
 *     API perdem o contexto)
 *   - Invalida cache React Query ao trocar (data stale doutro cliente)
 *   - Portaria SEMPRE tem 1 cliente (sem dropdown na UI) — mas mesmo
 *     assim setActiveClientContext é chamado pra header injetar
 *   - Default = primeiro cliente alfabético (consistência cross-session)
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { setActiveClientContext } from '@/lib/cliente-api';
import { useClienteAuth, type ClienteBinding } from './ClienteAuthContext';

const STORAGE_KEY = 'cliente_ativo_id';

interface ClienteContextValue {
  /** Cliente ativo (ou null se sem clientes vinculados). */
  clienteAtivo: ClienteBinding | null;
  /** Todos clientes vinculados ao user (multi-cliente Gestor). */
  clientesDisponiveis: ClienteBinding[];
  /** True se >= 2 clientes (mostra dropdown no header). */
  temMultiCliente: boolean;
  /** True se user é Gestor no clienteAtivo (controla rotas Gestor-only). */
  isGestor: boolean;
  /** True se user é Portaria no clienteAtivo. */
  isPortaria: boolean;
  /** Troca cliente ativo + persiste + invalida cache. */
  trocarCliente: (clientId: string) => void;
}

const ClienteContext = createContext<ClienteContextValue | null>(null);

function readStoredClienteId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null; // SSR / storage bloqueado
  }
}

function writeStoredClienteId(id: string | null) {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort
  }
}

/**
 * Resolve cliente ativo: storage → primeiro da lista → null.
 */
function resolveActive(clientes: ClienteBinding[]): ClienteBinding | null {
  if (clientes.length === 0) return null;
  const storedId = readStoredClienteId();
  if (storedId) {
    const match = clientes.find((c) => c.id === storedId);
    if (match) return match;
  }
  // Primeiro alfabético (clientes já vêm ordenados por nome do backend)
  return clientes[0];
}

export function ClienteProvider({ children }: { children: ReactNode }) {
  const { clientes, isAuthenticated } = useClienteAuth();
  const queryClient = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);

  // Sincroniza state com clientes do ClienteAuth (login, logout, multi-cliente)
  useEffect(() => {
    if (!isAuthenticated) {
      setActiveId(null);
      setActiveClientContext(null);
      return;
    }
    const ativo = resolveActive(clientes);
    if (ativo) {
      setActiveId(ativo.id);
      setActiveClientContext(ativo.id);
      writeStoredClienteId(ativo.id);
    } else {
      setActiveId(null);
      setActiveClientContext(null);
    }
  }, [isAuthenticated, clientes]);

  const trocarCliente = useCallback(
    (clientId: string) => {
      const valido = clientes.find((c) => c.id === clientId);
      if (!valido) {
        console.warn(`[ClienteContext] trocarCliente: id "${clientId}" não está em clientes`);
        return;
      }
      setActiveId(clientId);
      setActiveClientContext(clientId);
      writeStoredClienteId(clientId);
      // Invalida TODAS as queries — dados são por cliente, ao trocar tudo
      // que dependia do contexto anterior fica stale
      void queryClient.invalidateQueries();
    },
    [clientes, queryClient],
  );

  const value = useMemo<ClienteContextValue>(() => {
    const clienteAtivo = activeId ? (clientes.find((c) => c.id === activeId) ?? null) : null;
    return {
      clienteAtivo,
      clientesDisponiveis: clientes,
      temMultiCliente: clientes.length >= 2,
      isGestor: clienteAtivo?.role === 'gestor',
      isPortaria: clienteAtivo?.role === 'portaria',
      trocarCliente,
    };
  }, [activeId, clientes, trocarCliente]);

  return <ClienteContext.Provider value={value}>{children}</ClienteContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCliente(): ClienteContextValue {
  const ctx = useContext(ClienteContext);
  if (!ctx) {
    throw new Error('useCliente requer <ClienteProvider> no tree (depois de ClienteAuthProvider)');
  }
  return ctx;
}
